import { matchPath } from 'react-router';
import ROUTER, { PATH, PATH_RULES } from 'types/router';
import APP from 'types/app';
import { idSelector } from 'selectors/app';
import { isUserLoggedInSelector } from 'selectors/user';
import { isAvailableInfo, movieInfo } from 'selectors/movie';
import { subscriptionInfo } from 'selectors/subscription';
import { buyingTariffsSelector } from 'selectors/purchase';
import { constructGetParamsString, parseGetParams } from 'utils/routerUrl';
import { clearLastWatched, getLastWatched, saveLastWatched } from 'utils/serialObjectProgress';
import _ from 'lodash';
import appActions from 'actions/app';
import routerActions from 'actions/router';
import localStorageUtils from 'utils/localStorageUtils';
import { getIsUserLoading } from '../selectors/user';
import { ERROR_TYPE } from '../containers/ErrorPage/errorTypes';
import resolveMovieEnd from './lib/resolveMovieEnd';

const debug = self.debug('redux', 'middlewares', 'router');

let history;
let redirectPathObject;
let select;
let dispatch;
let historyArray = [];

export default (store) => (next) => (action) => {
  // declare select function using of 'store' closure
  select = (selector, params) => selector(store.getState(), params);
  dispatch = (act) => store.dispatch(act);

  switch (action.type) {
    case APP.INIT:
      const {router, searchQuery} = action.payload;
      const id = Number(searchQuery.id);

      history = router.history;

      if (id) {
        dispatch(appActions.setId(id));
        history.listen(onLocationChanged);
        onLocationChanged(history.location);
      } else {
        dispatch(routerActions.goToErrorPage(ERROR_TYPE.ID_ABSENT));
      }
      break;
    case ROUTER.GO_TO:
      goTo(action.payload);
      break;
    case ROUTER.GO_TO_ERROR:
      history.push({ ...history.location, ...action.payload });
      break;
    case ROUTER.GO_BACK:
      goBack(action);
      break;
    case ROUTER.TRY_TO_REDIRECT:
      tryToRedirect(action);
      break;
    case ROUTER.RESOLVE_MOVIE_END:
      const next = resolveMovieEnd(historyArray);
      next && goTo(next);
      break;
    default:
  }

  return next(action);
};

const goTo = (data) => {
  if (history) {
    const [{ path, parameters }, redirect] = checkPathWithinStore(data.path, data.parameters);
    redirectPathObject = redirect;
    const rules = PATH_RULES[path];
    if (rules) {
      const checkParamsResult = checkParams(rules, parameters);
      if (checkParamsResult.success) {
        let url;
        if (rules.isModal) {
          const getParams = history.location.search
            ? history.location.search
            : checkParamsResult.paramsString;
          url = `${history.location.pathname}${rules.url}${getParams}`;
        } else {
          url = `${rules.url}${checkParamsResult.paramsString}`;
        }
        history.push(url);
      } else {
        console.warn('router.goTo: Didn\'t provide all necessary parameters', parameters);
      }
    } else {
      console.warn('router.goTo: Bad PATH', path, parameters);
    }
  }
};

const goBack = () => {
  let historyObject = historyArray.pop();
  if (historyObject && historyArray.length > 0) {
    const currentRule = PATH_RULES[historyObject.path]; // throw current location and then check prev
    if (currentRule) {
      while (historyArray.length > 0) {
        historyObject = historyArray.pop();
        if (PATH_RULES[historyObject.path].backTransitionZone >= currentRule.backTransitionZone) {
          goTo(historyObject);
          break;
        }
      }
    }
  } else {
    goTo({ path: PATH.INFO });
  }
};

const tryToRedirect = () => {
  if (redirectPathObject) {
    const [destObject] = checkPathWithinStore(
      redirectPathObject.path,
      redirectPathObject.parameters,
    );
    goTo(destObject);
    redirectPathObject = null;
  } else {
    goTo({ path: PATH.INFO });
  }
};

const checkParams = (rules, parameters = {}) => {
  const result = {
    success: true,
    paramsString: '',
  };
  parameters = addIdIfAbsent(parameters);
  const paramsAccumulator = {};
  if (_.isArray(rules.requiredParams)) {
    const reqParams = rules.requiredParams;
    for (let i = 0, l = reqParams.length; i < l; ++i) {
      if (reqParams[i] === 'id') {
        paramsAccumulator.id = parameters.id || '';
      } else if (reqParams[i] in parameters) {
        paramsAccumulator[reqParams[i]] = parameters[reqParams[i]] || '';
        delete parameters[reqParams[i]];
      } else {
        result.success = false;
        break;
      }
    }
  }

  if (result.success && !_.isEmpty(parameters)) {
    const optParamsKeys = Object.keys(parameters);
    for (let i = 0, l = optParamsKeys.length; i < l; ++i) {
      if (parameters[optParamsKeys[i]] !== undefined) {
        paramsAccumulator[optParamsKeys[i]] = parameters[optParamsKeys[i]];
      }
    }
  }

  if (result.success && !_.isEmpty(paramsAccumulator)) {
    result.paramsString = constructGetParamsString(paramsAccumulator);
  }

  return result;
};

const checkPathWithinStore = (path, parameters = {}) => {
  let result = {
    path,
    parameters,
  };
  let redirectObject = null;
  const id = select(idSelector);
  const rules = PATH_RULES[path];
  if (rules) {
    switch (path) {
      case PATH.PAYMENT_EXECUTION:
      case PATH.PAYMENT_METHOD_SELECT:
      case PATH.MOVIE_TARIFF_SELECT:
      case PATH.SUBSCRIPTION_TARIFF_SELECT:
        debug('check access to restricted page: %s', path);
        const user = select(isUserLoggedInSelector);
        const userIsLoading = select(getIsUserLoading);
        if (!(user || userIsLoading)) {
          redirectObject = { path, parameters };
          result = {
            path: PATH.ENTER,
            parameters,
          };
          debug('access to page restricted, redirect to authentication');
        } else if (
          select(isAvailableInfo, { id }) ||
          _.get(select(subscriptionInfo, { id }), 'isBought')
        ) {
          result = {
            path: PATH.INFO,
            parameters,
          };
        } else {
          let tariffs;
          const params = {};
          if (path === PATH.SUBSCRIPTION_TARIFF_SELECT) {
            tariffs = select(buyingTariffsSelector, { type: 'subscription' });
            params.type = 'subscription';
          } else {
            // in this case can be only PATH.MOVIE_TARIFF_SELECT
            tariffs = select(buyingTariffsSelector, { type: parameters.type });
            params.type = parameters.type;
          }
          if (tariffs.length === 1) {
            params.tariff_id = tariffs[0].id;
            result = {
              path: PATH.PAYMENT_EXECUTION,
              parameters: params,
            };
          }
        }
        break;
      default:
    }
  }
  return [result, redirectObject];
};

const onLocationChanged = (location) => {
  debug('location changed. next path: %o, search: %o', location.pathname, location.search);
  checkId(location.search);
  checkLang(location.search);

  const pathObject = createPathObjectFromLocation(location);
  if (pathObject) {
    const redirect = checkPathWithinStore(pathObject.path, pathObject.parameters)[1];
    if (redirect) {
      goTo(pathObject);
    } else {
      let pushed = false;
      const rule = PATH_RULES[pathObject.path];
      if (pathObject.path === PATH.INFO) {
        const id = select(idSelector);
        const rootId = _.get(select(movieInfo, { id }), 'id');
        if (rootId && id !== rootId) {
          fixLastWatchedEpisode(rootId, id);
          goTo({ path: PATH.INFO, parameters: { id: rootId } });
        } else {
          historyArray.push(pathObject);
          pushed = true;
        }
      } else if (historyArray.length === 0 && rule.isModal) {
        goTo({ path: PATH.INFO });
      } else if (rule.unrestricted) {
        historyArray.push(pathObject);
        pushed = true;
      } else if (
        !rule.isModal &&
        historyArray.length >= 1 &&
        historyArray[historyArray.length - 1].path === pathObject.path
      ) {
        // avoid adding in history same paths
        // rewrite last item because there could be different parameters
        historyArray[historyArray.length - 1] = pathObject;
        pushed = true;
      } else if (
        !rule.isModal &&
        historyArray.length >= 2 &&
        historyArray[historyArray.length - 2].path === pathObject.path
      ) {
        // remove last two paths and add new one
        historyArray = historyArray.slice(0, historyArray.length - 2);
        historyArray.push(pathObject);
        pushed = true;
      } else {
        historyArray.push(pathObject);
        pushed = true;
      }
      if (pushed) {
        dispatch(appActions.setPathname(pathObject.path));
      }
    }
  } else {
    debug('No pathObject, go to InfoPage');
    goTo({ path: PATH.INFO });
  }
};

const checkId = (search) => {
  const currentId = select(idSelector);
  const searchParams = new URLSearchParams(search);
  /*
    if (!searchParams.get('id')) {
    // no way, at the moment of APP_INIT there are no history
      return dispatch(routerActions.goToErrorPage(ERROR_TYPE.ID_ABSENT));
    }
  */

  const id = Number(searchParams.get('id'));

  if (id !== currentId) {
    dispatch(appActions.setId(id));
  }
};

const checkReferer = (search) => {
  const referer = parseGetParams(search).referer || document.referrer || window.location.origin;
  if (referer) {
    window.__MEGOGO__referer__ = referer; // eslint-disable-line
  }
};

const checkLang = (search) => {
  const lang = parseGetParams(search).lang;
  if (lang) {
    localStorageUtils.setItem('lang', lang);
    return true;
  }
  return false;
};

const addIdIfAbsent = (parameters) => {
  const id = select(idSelector);
  if (!('id' in parameters) && id) {
    parameters = {
      ...parameters,
      id,
    };
  }
  return parameters;
};

const createPathObjectFromLocation = (location) => {
  const keys = Object.keys(PATH_RULES);
  let result = null;
  for (let i = 0, l = keys.length; i < l; ++i) {
    const matched = matchPath(location.pathname, PATH_RULES[keys[i]].url) ||
      (PATH_RULES[keys[i]].isModal && location.pathname.indexOf(PATH_RULES[keys[i]].url) !== -1);
    if (matched) {
      result = {
        path: keys[i],
        parameters: parseGetParams(location.search),
      };
      break;
    }
  }
  return result;
};

const fixLastWatchedEpisode = (id, fictionId) => {
  const lastWatched = getLastWatched(fictionId);
  if (fictionId) {
    clearLastWatched(fictionId);
  }
  if (lastWatched) {
    saveLastWatched(id, lastWatched);
  }
};

// initialize prime lang or set 'ru' by default
if (!checkLang(window.location.search)) {
  localStorageUtils.setItem('lang', localStorageUtils.getItem('lang'));
}

// check and set referer
checkReferer(window.location.search);
